import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PersonaAutocomplete from "./InfoInvoice/PersonaAutocomplete";
import CreditNoteInfo from "./InfoInvoice/CreditNoteInfo";
import DebitNoteInfo from "./InfoInvoice/DebitNoteInfo";
import RemissionGuideInfo from "./InfoInvoice/RemissionGuideInfo";
import RetentionInfo from "./InfoInvoice/RetentionInfo";

const tdLabelSx = {
  width: 200,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };

const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj ?? {});
  } catch {
    return "";
  }
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("access") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeCode = (value) => String(value || "").trim().padStart(3, "0");

const comprobanteOptions = [
  { label: "Factura", value: "Factura", backend: "FACTURA" },
  { label: "Nota de Crédito", value: "Nota de Crédito", backend: "NOTA_CREDITO" },
  { label: "Nota de Débito", value: "Nota de Débito", backend: "NOTA_DEBITO" },
  { label: "Guía de Remisión", value: "Guía de Remisión", backend: "GUIA_REMISION" },
  { label: "Retención", value: "Retención", backend: "RETENCION" },
];

const comprobanteToBackend = (label) => {
  const found = comprobanteOptions.find((x) => x.value === label);
  return found?.backend || "FACTURA";
};

const buildPersonaFromInitial = (initialData) => {
  if (!initialData) return null;

  if (initialData.persona && typeof initialData.persona === "object") {
    return {
      id:
        initialData.persona.id ||
        initialData.persona.identificacion ||
        initialData.persona.razon_social ||
        "",
      identificacion: String(initialData.persona.identificacion || "").trim(),
      razon_social: String(initialData.persona.razon_social || "").trim(),
      correo: String(initialData.persona.correo || "").trim(),
      telefono: String(initialData.persona.telefono || "").trim(),
      direccion: String(initialData.persona.direccion || "").trim(),
    };
  }

  const identificacion = String(initialData.identificacion_cliente || "").trim();
  const razon_social = String(initialData.cliente || "").trim();

  if (!identificacion && !razon_social) return null;

  return {
    id: identificacion || razon_social,
    identificacion,
    razon_social,
    correo: String(initialData.email || "").trim(),
    telefono: String(initialData.telefono || "").trim(),
    direccion: String(initialData.direccion || "").trim(),
  };
};

const buildGuiaRemisionData = (base, patch = {}) => {
  const data = { ...base, ...patch };
  const transportista = data.guia_transportista || null;
  const fechaInicio = data.guia_fecha_inicio || data.fecha_emision || "";
  const fechaFin = data.guia_fecha_fin || fechaInicio || "";

  return {
    transportista_identificacion:
      data.transportista_identificacion ||
      transportista?.identificacion ||
      transportista?.ruc ||
      transportista?.cedula ||
      "",
    transportista_razon_social:
      data.transportista_razon_social ||
      transportista?.razon_social ||
      transportista?.nombre_comercial ||
      "",
    placa: data.guia_placa || "",
    direccion_partida: data.guia_punto_partida || "",
    direccion_destino: data.guia_punto_llegada || "",
    motivo_traslado: data.guia_motivo_traslado || "",
    ruta: data.guia_ruta || "",
    fecha_inicio_transporte: fechaInicio,
    fecha_fin_transporte: fechaFin,
    numero_documento_sustento: data.documento_relacionado_numero || "",
    fecha_documento_sustento: data.documento_relacionado_fecha || "",
  };
};

const getInitialDataState = () => ({
  fecha_emision: "",
  tipo_documento: "Cliente",
  tipo_persona: "CLIENTE",
  tipo_comprobante: "Factura",
  tipo_documento_backend: "FACTURA",
  puntos_emision: [],
  establecimiento: "",
  punto_emision: "",
  numero_documento: "",
  persona: null,
  referencia: "",
  vendedor: "",
  vencimiento_dias: "0",
  entregado: true,
  documento_relacionado_id: "",
  documento_relacionado_numero: "",
  documento_relacionado_clave_acceso: "",
  documento_relacionado_tipo: "",
  documento_relacionado_fecha: "",
  factura_relacionada_id: "",
  factura_relacionada_numero: "",
  factura_relacionada_clave_acceso: "",
  nota_credito_motivo_tipo: "DEVOLUCION",
  tipo_nota_debito: "OTROS",
  nota_debito_motivo: "",
  motivo_nota_debito: "",
  es_devolucion: true,
  es_ajuste: false,
  guia_motivo_traslado: "",
  guia_punto_partida: "",
  guia_punto_llegada: "",
  guia_fecha_inicio: "",
  guia_fecha_fin: "",
  guia_transportista: null,
  guia_placa: "",
  guia_ruta: "",
  guia_remision: {
    transportista_identificacion: "",
    transportista_razon_social: "",
    placa: "",
    direccion_partida: "",
    direccion_destino: "",
    motivo_traslado: "",
    ruta: "",
    fecha_inicio_transporte: "",
    fecha_fin_transporte: "",
    numero_documento_sustento: "",
    fecha_documento_sustento: "",
  },
  retencion_periodo_fiscal: "",
  retencion_documento_sustento_id: "",
  retencion_documento_sustento_numero: "",
  retencion_documento_sustento_clave: "",
  retencion_fecha_documento_sustento: "",
});

export default function InfoInvoice({ onChange, initialData, onProductsLoaded }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState(getInitialDataState);
  const [fechaError, setFechaError] = useState(false);
  const [vendedores, setVendedores] = useState([]);

  const hydratingRef = useRef(false);
  const lastInitialSigRef = useRef("");

  const isFactura = data.tipo_comprobante === "Factura";
  const isNotaCredito = data.tipo_comprobante === "Nota de Crédito";
  const isNotaDebito = data.tipo_comprobante === "Nota de Débito";
  const isGuia = data.tipo_comprobante === "Guía de Remisión";
  const isRetencion = data.tipo_comprobante === "Retención";

  const establecimientosOptions = useMemo(() => {
    const map = new Map();

    (data.puntos_emision || []).forEach((p) => {
      const codigo = normalizeCode(p.establecimiento);
      if (!codigo) return;

      if (!map.has(codigo)) {
        map.set(codigo, {
          codigo,
          nombre: String(p.establecimiento_nombre || `ESTABLECIMIENTO ${codigo}`),
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.codigo.localeCompare(b.codigo));
  }, [data.puntos_emision]);

  const puntosFiltrados = useMemo(() => {
    return (data.puntos_emision || [])
      .filter((p) => normalizeCode(p.establecimiento) === normalizeCode(data.establecimiento))
      .sort((a, b) =>
        normalizeCode(a.punto_emision).localeCompare(normalizeCode(b.punto_emision))
      );
  }, [data.puntos_emision, data.establecimiento]);

  useEffect(() => {
    const sig = safeStringify(initialData);
    if (!initialData) return;
    if (sig && sig === lastInitialSigRef.current) return;

    lastInitialSigRef.current = sig;
    hydratingRef.current = true;

    const personaPrefill = buildPersonaFromInitial(initialData);

    const tipoComprobante =
      initialData.tipo_comprobante ||
      initialData.tipo_documento_label ||
      data.tipo_comprobante;

    const tipoBackend =
      initialData.tipo_documento_backend ||
      comprobanteToBackend(tipoComprobante);

    const motivoTipo =
      initialData.nota_credito_motivo_tipo ||
      (initialData.es_ajuste ? "AJUSTE" : "DEVOLUCION");

    setData((prev) => {
      const merged = {
        ...prev,
        ...initialData,
        puntos_emision: Array.isArray(initialData.puntos_emision)
          ? initialData.puntos_emision
          : prev.puntos_emision,
        persona: personaPrefill ?? prev.persona,
        tipo_persona: initialData.tipo_persona || initialData.tipo_documento || prev.tipo_persona,
        tipo_comprobante: tipoComprobante,
        tipo_documento_backend: tipoBackend,
        referencia: initialData.referencia ?? prev.referencia,
        vendedor: initialData.vendedor ?? prev.vendedor,
        establecimiento: initialData.establecimiento ?? prev.establecimiento,
        punto_emision: initialData.punto_emision ?? prev.punto_emision,
        numero_documento: initialData.numero_documento ?? prev.numero_documento,
        fecha_emision: initialData.fecha_emision ?? prev.fecha_emision,
        vencimiento_dias:
          initialData.vencimiento_dias != null
            ? String(initialData.vencimiento_dias)
            : prev.vencimiento_dias,
        entregado:
          typeof initialData.entregado === "boolean" ? initialData.entregado : prev.entregado,
        documento_relacionado_id:
          initialData.documento_relacionado_id != null
            ? String(initialData.documento_relacionado_id)
            : prev.documento_relacionado_id,
        documento_relacionado_numero:
          initialData.documento_relacionado_numero ?? prev.documento_relacionado_numero,
        documento_relacionado_clave_acceso:
          initialData.documento_relacionado_clave_acceso ?? prev.documento_relacionado_clave_acceso,
        documento_relacionado_tipo:
          initialData.documento_relacionado_tipo ?? prev.documento_relacionado_tipo,
        documento_relacionado_fecha:
          initialData.documento_relacionado_fecha ?? prev.documento_relacionado_fecha,
        factura_relacionada_id:
          initialData.factura_relacionada_id != null
            ? String(initialData.factura_relacionada_id)
            : prev.factura_relacionada_id,
        factura_relacionada_numero:
          initialData.factura_relacionada_numero ?? prev.factura_relacionada_numero,
        factura_relacionada_clave_acceso:
          initialData.factura_relacionada_clave_acceso ?? prev.factura_relacionada_clave_acceso,
        nota_credito_motivo_tipo: motivoTipo,
        tipo_nota_debito: initialData.tipo_nota_debito || prev.tipo_nota_debito || "OTROS",
        nota_debito_motivo: initialData.nota_debito_motivo ?? prev.nota_debito_motivo,
        motivo_nota_debito: initialData.motivo_nota_debito ?? prev.motivo_nota_debito,
        es_devolucion: motivoTipo === "DEVOLUCION",
        es_ajuste: motivoTipo === "AJUSTE",
      };

      if (merged.tipo_comprobante === "Guía de Remisión" || merged.tipo_documento_backend === "GUIA_REMISION") {
        merged.guia_fecha_inicio = merged.guia_fecha_inicio || merged.fecha_emision || "";
        merged.fecha_emision = merged.guia_fecha_inicio || merged.fecha_emision || "";
      }

      return {
        ...merged,
        guia_remision: initialData.guia_remision || buildGuiaRemisionData(merged),
      };
    });

    setTimeout(() => {
      hydratingRef.current = false;
    }, 0);
  }, [initialData]);

  useEffect(() => {
    const cargarVendedores = async () => {
      try {
        const r = await fetch("/facturas/usuarios/activos/", {
          credentials: "include",
          headers: {
            ...getAuthHeaders(),
          },
        });

        const j = await r.json().catch(() => ({}));
        const lista = r.ok && Array.isArray(j.vendedores) ? j.vendedores : [];

        setVendedores(lista);

        if (lista.length > 0) {
          setData((prev) => ({
            ...prev,
            vendedor: prev.vendedor || String(lista[0].id),
          }));
        }
      } catch {
        setVendedores([]);
      }
    };

    cargarVendedores();
  }, []);

  useEffect(() => {
    const cargarDetalle = async () => {
      try {
        const tipoDocumentoBackend = comprobanteToBackend(data.tipo_comprobante);

        const res = await fetch(
          `/facturas/documento/detalle/?tipo_documento=${encodeURIComponent(tipoDocumentoBackend)}`,
          {
            credentials: "include",
            headers: {
              ...getAuthHeaders(),
            },
          }
        );

        const json = await res.json().catch(() => ({}));
        if (!res.ok) return;

        const puntos = Array.isArray(json.puntos_emision) ? json.puntos_emision : [];

        hydratingRef.current = true;

        setData((prev) => {
          const establecimientoActual = normalizeCode(prev.establecimiento);
          const puntoActual = normalizeCode(prev.punto_emision);

          const puntoExistente = puntos.find(
            (p) =>
              normalizeCode(p.establecimiento) === establecimientoActual &&
              normalizeCode(p.punto_emision) === puntoActual
          );

          const puntosDelEstablecimiento = puntos.filter(
            (p) => normalizeCode(p.establecimiento) === establecimientoActual
          );

          const primeroGlobal = puntos[0] || null;
          const primeroMismoEstablecimiento = puntosDelEstablecimiento[0] || null;
          const seleccionado = puntoExistente || primeroMismoEstablecimiento || primeroGlobal;

          const next = {
            ...prev,
            fecha_emision: prev.fecha_emision || json.fecha_emision || "",
            tipo_comprobante: prev.tipo_comprobante,
            tipo_documento_backend: tipoDocumentoBackend,
            puntos_emision: puntos,
            establecimiento: seleccionado ? normalizeCode(seleccionado.establecimiento) : "",
            punto_emision: seleccionado ? normalizeCode(seleccionado.punto_emision) : "",
            numero_documento: seleccionado?.numero_documento || "",
          };

          if (tipoDocumentoBackend === "GUIA_REMISION") {
            next.guia_fecha_inicio = next.guia_fecha_inicio || next.fecha_emision || "";
            next.fecha_emision = next.guia_fecha_inicio || next.fecha_emision || "";
            next.guia_remision = buildGuiaRemisionData(next);
          }

          return next;
        });

        setTimeout(() => {
          hydratingRef.current = false;
        }, 0);
      } catch {}
    };

    cargarDetalle();
  }, [data.tipo_comprobante]);

  useEffect(() => {
    if (!data.establecimiento) return;

    const existePunto = puntosFiltrados.some(
      (p) => normalizeCode(p.punto_emision) === normalizeCode(data.punto_emision)
    );

    if (!existePunto && puntosFiltrados.length > 0) {
      const primero = puntosFiltrados[0];

      setData((prev) => ({
        ...prev,
        punto_emision: normalizeCode(primero.punto_emision),
        numero_documento: primero.numero_documento || "",
      }));
    }

    if (puntosFiltrados.length === 0 && data.punto_emision) {
      setData((prev) => ({
        ...prev,
        punto_emision: "",
        numero_documento: "",
      }));
    }
  }, [data.establecimiento, data.punto_emision, puntosFiltrados]);

  useEffect(() => {
    if (hydratingRef.current) return;
    onChange?.(data);
  }, [data, onChange]);

  const patchData = (patch) => {
    setData((prev) => {
      const next = { ...prev, ...patch };

      const esGuia =
        next.tipo_comprobante === "Guía de Remisión" ||
        next.tipo_documento_backend === "GUIA_REMISION";

      if (esGuia) {
        if (patch.fecha_emision !== undefined) {
          next.guia_fecha_inicio = patch.fecha_emision;
        }

        if (patch.guia_fecha_inicio !== undefined) {
          next.fecha_emision = patch.guia_fecha_inicio;
        }

        if (!next.guia_fecha_inicio && next.fecha_emision) {
          next.guia_fecha_inicio = next.fecha_emision;
        }

        if (!next.fecha_emision && next.guia_fecha_inicio) {
          next.fecha_emision = next.guia_fecha_inicio;
        }
      }

      if (
        esGuia ||
        patch.guia_motivo_traslado !== undefined ||
        patch.guia_punto_partida !== undefined ||
        patch.guia_punto_llegada !== undefined ||
        patch.guia_fecha_inicio !== undefined ||
        patch.guia_fecha_fin !== undefined ||
        patch.guia_transportista !== undefined ||
        patch.guia_placa !== undefined ||
        patch.guia_ruta !== undefined ||
        patch.documento_relacionado_numero !== undefined ||
        patch.documento_relacionado_fecha !== undefined
      ) {
        next.guia_remision = buildGuiaRemisionData(next);
      }

      return next;
    });
  };

  const clearRelatedDocumentPatch = {
    documento_relacionado_id: "",
    documento_relacionado_numero: "",
    documento_relacionado_clave_acceso: "",
    documento_relacionado_tipo: "",
    documento_relacionado_fecha: "",
    factura_relacionada_id: "",
    factura_relacionada_numero: "",
    factura_relacionada_clave_acceso: "",
  };

  const handleFechaChange = (e) => {
    const value = e.target.value;
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;

    setFechaError(value ? !regex.test(value) : false);

    if (isGuia) {
      patchData({
        fecha_emision: value,
        guia_fecha_inicio: value,
      });
      return;
    }

    patchData({ fecha_emision: value });
  };

  const handleEstablecimientoChange = (e) => {
    const seleccionado = normalizeCode(e.target.value);

    const puntosDelEst = data.puntos_emision.filter(
      (p) => normalizeCode(p.establecimiento) === seleccionado
    );

    const primero = puntosDelEst[0];

    patchData({
      establecimiento: seleccionado,
      punto_emision: primero ? normalizeCode(primero.punto_emision) : "",
      numero_documento: primero ? primero.numero_documento : "",
    });
  };

  const handlePuntoChange = (e) => {
    const seleccionado = normalizeCode(e.target.value);

    const punto = data.puntos_emision.find(
      (p) =>
        normalizeCode(p.establecimiento) === normalizeCode(data.establecimiento) &&
        normalizeCode(p.punto_emision) === seleccionado
    );

    patchData({
      punto_emision: seleccionado,
      numero_documento: punto ? punto.numero_documento : "",
    });
  };

  const handleTipoPersonaChange = (e) => {
    patchData({
      tipo_persona: e.target.value,
      persona: null,
      ...clearRelatedDocumentPatch,
    });
  };

  const handleTipoComprobanteChange = (e) => {
    const nuevoTipo = e.target.value;
    const backend = comprobanteToBackend(nuevoTipo);

    const nuevoTipoPersona =
      nuevoTipo === "Retención" ? "PROVEEDOR" : "CLIENTE";

    hydratingRef.current = true;

    setData((prev) => {
      const fechaBase = backend === "GUIA_REMISION" ? prev.fecha_emision || prev.guia_fecha_inicio || "" : "";

      const next = {
        ...prev,
        tipo_comprobante: nuevoTipo,
        tipo_documento_backend: backend,
        tipo_persona: nuevoTipoPersona,
        persona: null,
        puntos_emision: [],
        establecimiento: "",
        punto_emision: "",
        numero_documento: "",
        documento_relacionado_id: "",
        documento_relacionado_numero: "",
        documento_relacionado_clave_acceso: "",
        documento_relacionado_tipo: "",
        documento_relacionado_fecha: "",
        factura_relacionada_id: "",
        factura_relacionada_numero: "",
        factura_relacionada_clave_acceso: "",
        nota_credito_motivo_tipo: "DEVOLUCION",
        tipo_nota_debito: "OTROS",
        nota_debito_motivo: "",
        motivo_nota_debito: "",
        es_devolucion: true,
        es_ajuste: false,
        guia_motivo_traslado: "",
        guia_punto_partida: "",
        guia_punto_llegada: "",
        guia_fecha_inicio: fechaBase,
        guia_fecha_fin: fechaBase,
        guia_transportista: null,
        guia_placa: "",
        guia_ruta: "",
        guia_remision: {
          transportista_identificacion: "",
          transportista_razon_social: "",
          placa: "",
          direccion_partida: "",
          direccion_destino: "",
          motivo_traslado: "",
          ruta: "",
          fecha_inicio_transporte: fechaBase,
          fecha_fin_transporte: fechaBase,
          numero_documento_sustento: "",
          fecha_documento_sustento: "",
        },
        retencion_periodo_fiscal: "",
        retencion_documento_sustento_id: "",
        retencion_documento_sustento_numero: "",
        retencion_documento_sustento_clave: "",
        retencion_fecha_documento_sustento: "",
      };

      return next;
    });

    setTimeout(() => {
      hydratingRef.current = false;
    }, 0);
  };

  const handleRelatedDocumentSelected = (doc, productos) => {
    const patch = {
      documento_relacionado_id: doc?.id ? String(doc.id) : "",
      documento_relacionado_numero: doc?.numero_documento || doc?.numero || "",
      documento_relacionado_clave_acceso: doc?.clave_acceso || "",
      documento_relacionado_tipo: doc?.tipo_documento || doc?.tipo || "FACTURA",
      documento_relacionado_fecha: doc?.fecha_emision || "",
      factura_relacionada_id: doc?.id ? String(doc.id) : "",
      factura_relacionada_numero: doc?.numero_documento || doc?.numero || "",
      factura_relacionada_clave_acceso: doc?.clave_acceso || "",
      documento_modificado_id: doc?.id ? String(doc.id) : "",
    };

    patchData(patch);

    if (Array.isArray(productos)) {
      onProductsLoaded?.(productos);
    }
  };

  const personaLabel = isGuia ? "Destinatario:" : isRetencion ? "Proveedor:" : "Persona:";
  const tipoPersonaLabel = isGuia ? "Tipo de Destinatario:" : "Tipo de Persona:";

  const fechaHelperText = fechaError
    ? "Formato inválido (dd/mm/aaaa)"
    : isGuia
    ? "En guías, esta fecha será igual a la fecha de inicio de traslado"
    : "";

  const renderDynamicSection = (mobile = false) => {
    if (isNotaCredito) {
      return (
        <CreditNoteInfo
          persona={data.persona}
          facturaRelacionadaId={data.factura_relacionada_id}
          facturaRelacionadaNumero={data.factura_relacionada_numero}
          facturaRelacionadaClaveAcceso={data.factura_relacionada_clave_acceso}
          motivoTipo={data.nota_credito_motivo_tipo}
          onDocumentSelected={handleRelatedDocumentSelected}
          onChange={(patch) => patchData(patch)}
          mobile={mobile}
        />
      );
    }

    if (isNotaDebito) {
      return (
        <DebitNoteInfo
          persona={data.persona}
          documentoRelacionadoId={data.documento_relacionado_id}
          documentoRelacionadoNumero={data.documento_relacionado_numero}
          documentoRelacionadoClaveAcceso={data.documento_relacionado_clave_acceso}
          tipoNotaDebito={data.tipo_nota_debito}
          motivo={data.motivo_nota_debito || data.nota_debito_motivo}
          onDocumentSelected={handleRelatedDocumentSelected}
          onChange={(patch) => patchData(patch)}
          mobile={mobile}
        />
      );
    }

    if (isGuia) {
      return (
        <RemissionGuideInfo
          data={data}
          onChange={(patch) => patchData(patch)}
          onDocumentSelected={handleRelatedDocumentSelected}
          mobile={mobile}
        />
      );
    }

    if (isRetencion) {
      return (
        <RetentionInfo
          data={data}
          onChange={(patch) => patchData(patch)}
          onDocumentSelected={handleRelatedDocumentSelected}
          mobile={mobile}
        />
      );
    }

    return null;
  };

  if (isMobile) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#333" }}>
          Información del Documento
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Fecha de Emisión
            </Typography>
            <TextField
              fullWidth
              value={data.fecha_emision}
              onChange={handleFechaChange}
              error={fechaError}
              helperText={fechaHelperText}
              placeholder="dd/mm/aaaa"
              inputProps={{ maxLength: 10 }}
            />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              {tipoPersonaLabel.replace(":", "")}
            </Typography>
            <Select
              fullWidth
              value={data.tipo_persona}
              onChange={handleTipoPersonaChange}
              disabled={isRetencion}
            >
              <MenuItem value="CLIENTE">Cliente</MenuItem>
              <MenuItem value="PROVEEDOR">Proveedor</MenuItem>
            </Select>
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Tipo de Comprobante
            </Typography>
            <Select fullWidth value={data.tipo_comprobante} onChange={handleTipoComprobanteChange}>
              {comprobanteOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Establecimiento
            </Typography>
            <Select fullWidth value={data.establecimiento} onChange={handleEstablecimientoChange}>
              {establecimientosOptions.map((e, i) => (
                <MenuItem key={`${e.codigo}-${i}`} value={e.codigo}>
                  {e.codigo} - {e.nombre}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              P. Emisión
            </Typography>
            <Select fullWidth value={data.punto_emision} onChange={handlePuntoChange}>
              {puntosFiltrados.map((p, i) => (
                <MenuItem
                  key={`${p.establecimiento}-${p.punto_emision}-${i}`}
                  value={normalizeCode(p.punto_emision)}
                >
                  {normalizeCode(p.punto_emision)}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              # de Documento
            </Typography>
            <TextField fullWidth value={data.numero_documento} InputProps={{ readOnly: true }} />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              {personaLabel.replace(":", "")}
            </Typography>
            <PersonaAutocomplete
              value={data.persona}
              tipoDocumento={data.tipo_persona}
              onChange={(persona) =>
                patchData({
                  persona,
                  ...clearRelatedDocumentPatch,
                })
              }
              mobile
            />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Referencia
            </Typography>
            <TextField
              fullWidth
              value={data.referencia}
              onChange={(e) => patchData({ referencia: e.target.value })}
            />
          </Box>

          <Box sx={{ p: 1.25, borderBottom: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
              Vendedor
            </Typography>
            <Select
              fullWidth
              value={data.vendedor}
              onChange={(e) => patchData({ vendedor: e.target.value })}
            >
              {vendedores.map((v) => (
                <MenuItem key={v.id} value={String(v.id)}>
                  {v.nombre}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {!isRetencion && (
            <Box sx={{ p: 1.25, borderBottom: !isFactura ? "1px solid #e0e0e0" : "none" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#555", mb: 0.75 }}>
                Vencimiento
              </Typography>
              <TextField
                fullWidth
                value={data.vencimiento_dias}
                onChange={(e) =>
                  patchData({
                    vencimiento_dias: e.target.value.replace(/\D/g, "").slice(0, 3) || "0",
                  })
                }
              />
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Checkbox
                  checked={data.entregado}
                  onChange={(e) => patchData({ entregado: e.target.checked })}
                />
                <Typography sx={{ fontSize: 14, color: "#333" }}>Entregado</Typography>
              </Box>
            </Box>
          )}

          {!isFactura && <Box sx={{ p: 1.25 }}>{renderDynamicSection(true)}</Box>}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#333" }}>
        Información del Documento
      </Typography>

      <Table>
        <TableBody>
          <TableRow>
            <TableCell sx={tdLabelSx}>Fecha de Emisión:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.fecha_emision}
                onChange={handleFechaChange}
                error={fechaError}
                helperText={fechaHelperText}
                placeholder="dd/mm/aaaa"
                inputProps={{ maxLength: 10 }}
                sx={{ minWidth: 280 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>{tipoPersonaLabel}</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.tipo_persona}
                onChange={handleTipoPersonaChange}
                disabled={isRetencion}
                sx={{ minWidth: 280 }}
              >
                <MenuItem value="CLIENTE">Cliente</MenuItem>
                <MenuItem value="PROVEEDOR">Proveedor</MenuItem>
              </Select>
            </TableCell>

            <TableCell sx={{ ...tdFieldSx, pl: 2 }}>
              <Select
                value={data.tipo_comprobante}
                onChange={handleTipoComprobanteChange}
                sx={{ minWidth: 280 }}
              >
                {comprobanteOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Establecimiento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.establecimiento}
                onChange={handleEstablecimientoChange}
                sx={{ minWidth: 280 }}
              >
                {establecimientosOptions.map((e, i) => (
                  <MenuItem key={`${e.codigo}-${i}`} value={e.codigo}>
                    {e.codigo} - {e.nombre}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>P. Emisión:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.punto_emision}
                onChange={handlePuntoChange}
                sx={{ minWidth: 280 }}
              >
                {puntosFiltrados.map((p, i) => (
                  <MenuItem
                    key={`${p.establecimiento}-${p.punto_emision}-${i}`}
                    value={normalizeCode(p.punto_emision)}
                  >
                    {normalizeCode(p.punto_emision)}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}># de Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.numero_documento}
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 280 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>{personaLabel}</TableCell>
            <TableCell sx={tdFieldSx}>
              <PersonaAutocomplete
                value={data.persona}
                tipoDocumento={data.tipo_persona}
                onChange={(persona) =>
                  patchData({
                    persona,
                    ...clearRelatedDocumentPatch,
                  })
                }
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Referencia:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.referencia}
                onChange={(e) => patchData({ referencia: e.target.value })}
                sx={{ minWidth: 350 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Vendedor:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.vendedor}
                onChange={(e) => patchData({ vendedor: e.target.value })}
                sx={{ minWidth: 280 }}
              >
                {vendedores.map((v) => (
                  <MenuItem key={v.id} value={String(v.id)}>
                    {v.nombre}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          {!isRetencion && (
            <TableRow>
              <TableCell sx={tdLabelSx}>Vencimiento / Entregado:</TableCell>
              <TableCell sx={tdFieldSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    value={data.vencimiento_dias}
                    onChange={(e) =>
                      patchData({
                        vencimiento_dias: e.target.value.replace(/\D/g, "").slice(0, 3) || "0",
                      })
                    }
                    sx={{ width: 120 }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={data.entregado}
                      onChange={(e) => patchData({ entregado: e.target.checked })}
                    />
                    <Typography>Entregado</Typography>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}

          {!isFactura && (
            <TableRow>
              <TableCell sx={tdLabelSx}>
                {isNotaCredito && "Nota de Crédito:"}
                {isNotaDebito && "Nota de Débito:"}
                {isGuia && "Guía de Remisión:"}
                {isRetencion && "Retención:"}
              </TableCell>
              <TableCell sx={tdFieldSx}>{renderDynamicSection(false)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}